/**
 * CrudResult -- Standard CRUD response format.
 * Convention: { ok: true/false, data?, error?, code?, meta?, details? }
 *
 * @version v1 (extracted from v2.1)
 * @module domain/value-objects/CrudResult
 */

export function ok(data, meta) {
  const result = { ok: true, data };
  if (meta !== undefined) result.meta = meta;
  return result;
}

export function fail(code, message, details) {
  const result = { ok: false, error: message };
  if (code) result.code = code;
  if (details !== undefined) result.details = details;
  return result;
}

/**
 * Extrae el mensaje legible de un CrudResult fallido.
 *
 * MV-T2b (bit #3442): existen DOS shapes de error conviviendo y leer el campo
 * equivocado hace que el usuario vea "(error)" en vez del motivo real:
 *   - `fail()` de aqui  -> error es un STRING plano ("Ya existe un movimiento...")
 *   - varios ApiClient  -> error es un OBJETO ({ code: 'BUSINESS_REQUIRED' })
 * Hacer `res.error?.message` funciona con el segundo y devuelve undefined con el
 * primero, que es justo el que trae el mensaje del backend. Este helper resuelve
 * ambos y solo cae al fallback cuando de verdad no hay nada que decir.
 *
 * @param {{error?:any, code?:any}} result
 * @param {string} fallback  Texto cuando no hay mensaje ni codigo aprovechable.
 */
export function errorMessage(result, fallback = 'error desconocido') {
  if (!result) return fallback;
  const e = result.error;
  if (typeof e === 'string' && e.trim()) return e.trim();
  if (e && typeof e === 'object') {
    const m = e.message || e.detail || e.mensaje || e.title;
    if (typeof m === 'string' && m.trim()) return m.trim();
    if (e.code) return String(e.code);
  }
  if (result.code) return String(result.code);
  return fallback;
}

/**
 * Status HTTP de un CrudResult fallido, mirando los sitios donde puede quedar.
 * `fail(res.status, msg)` lo deja en `code`; algunos clientes lo anidan en error.
 */
export function errorStatus(result) {
  const raw = result?.status ?? result?.code ?? result?.error?.status ?? result?.error?.code;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

/**
 * Convierte un CrudResult fallido en excepcion (para flujos try/catch).
 * ApiBase NUNCA lanza: devuelve { ok:false }. Las vistas que envuelven
 * escrituras en try/catch DEBEN pasar el resultado por aqui — si no, un
 * rechazo de la API se trata como exito silencioso (bug cazado en la
 * auditoria de colaboradores 2026-07-20: asistencia y horarios cerraban
 * el modal y refrescaban como si hubieran guardado).
 */
export function unwrap(res, fallback = 'La operacion fue rechazada') {
  if (!res || res.ok !== true) {
    throw new Error(res?.error || fallback);
  }
  return res;
}
